import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth-store';
import { useUIStore } from '../store/ui-store';
import { Avatar } from './Avatar';
import { useTranslation } from 'react-i18next';
export function Sidebar({ isOpen = false, onClose }) {
    const location = useLocation();
    const { projectId } = useParams();
    const navRef = useRef(null);
    const user = useAuthStore((state) => state.user);
    const logout = useAuthStore((state) => state.logout);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const theme = useUIStore((state) => state.theme);
    const isDark = theme.includes('night');
    const navItems = [
        { path: '/', label: t('nav.dashboard'), icon: '📊' },
        { path: '/my-tasks', label: t('nav.myTasks'), icon: '✅' },
        { path: '/activity', label: t('nav.activity'), icon: '🔔' },
        { path: '/settings', label: t('nav.settings'), icon: '⚙️' },
    ];
    const adminNavItems = [
        { path: '/admin', label: t('nav.adminOverview'), icon: '🛡️' },
        { path: '/admin/users', label: t('nav.userManagement'), icon: '👥' },
        { path: '/admin/projects', label: t('nav.projectSetup'), icon: '📁' },
    ];
    const isAdmin = user?.role === 'admin';
    const projectNavItems = [
        { key: 'overview', label: t('nav.overview'), icon: '📝' },
        { key: 'team', label: t('nav.team'), icon: '👥' },
        { key: 'time', label: t('nav.timeTracking'), icon: '⏱️' },
        { key: 'reports', label: t('nav.reports'), icon: '📄' },
        { key: 'charts', label: t('nav.charts'), icon: '📈' },
        { key: 'changelog', label: t('nav.changeLog'), icon: '📋' },
    ];
    const trapFocus = useCallback((e) => {
        if (!isOpen || !navRef.current)
            return;
        const focusableElements = navRef.current.querySelectorAll('a[href], button, [tabindex]:not([tabindex="-1"])');
        if (focusableElements.length === 0)
            return;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (e.key === 'Tab') {
            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            }
            else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        }
        if (e.key === 'Escape') {
            onClose?.();
        }
    }, [isOpen, onClose]);
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', trapFocus);
            const firstLink = navRef.current?.querySelector('a[href]');
            firstLink?.focus();
        }
        return () => document.removeEventListener('keydown', trapFocus);
    }, [isOpen, trapFocus]);
    const handleLogout = () => {
        logout();
        navigate('/login');
    };
    return (_jsxs(_Fragment, { children: [isOpen && (_jsx("div", { className: "fixed inset-0 bg-black/50 z-30 md:hidden", onClick: onClose, "aria-hidden": "true" })), _jsxs("aside", { ref: navRef, className: `
          fixed inset-y-0 z-40 w-64 bg-bg-secondary flex flex-col
          transition-transform duration-200 ease-in-out
          ${isRTL
                    ? 'right-0 border-l border-card-border'
                    : 'left-0 border-r border-card-border'}
          ${isOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'}
          md:relative md:translate-x-0 md:z-auto
        `, children: [_jsx("div", { className: "p-4 border-b border-card-border flex items-center justify-start", children: _jsx("img", { src: isDark ? '/LOGO-white.svg' : '/LOGO.svg', alt: "ProjectManMD", className: "h-10 w-auto" }) }), _jsxs("nav", { role: "navigation", "aria-label": t('accessibility.mainNav'), className: "flex-1 p-4", children: [_jsx("ul", { className: "space-y-2", children: navItems.map((item) => {
                                    const isActive = location.pathname === item.path;
                                    return (_jsx("li", { children: _jsxs(Link, { to: item.path, "aria-current": isActive ? 'page' : undefined, className: `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive
                                                ? 'bg-primary text-white'
                                                : 'text-text-secondary hover:bg-bg-primary'}`, children: [_jsx("span", { "aria-hidden": "true", children: item.icon }), _jsx("span", { children: item.label })] }) }, item.path));
                                }) }), user && isAdmin && (_jsxs(_Fragment, { children: [_jsx("div", { className: "my-3 border-t border-card-border" }), _jsx("p", { className: "text-xs text-text-muted font-medium uppercase tracking-wide mb-2 px-3", children: t('nav.admin') }), _jsx("ul", { className: "space-y-2", children: adminNavItems.map((item) => {
                                            const isActive = location.pathname === item.path;
                                            return (_jsx("li", { children: _jsxs(Link, { to: item.path, "aria-current": isActive ? 'page' : undefined, className: `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive
                                                        ? 'bg-primary text-white'
                                                        : 'text-text-secondary hover:bg-bg-primary'}`, children: [_jsx("span", { "aria-hidden": "true", children: item.icon }), _jsx("span", { children: item.label })] }) }, item.path));
                                        }) })] })), projectId && (_jsxs(_Fragment, { children: [_jsx("div", { className: "my-3 border-t border-card-border" }), _jsx("ul", { className: "space-y-2", children: projectNavItems.map((item) => {
                                            const path = `/${item.key}/${projectId}`;
                                            const isActive = location.pathname === path;
                                            return (_jsx("li", { children: _jsxs(Link, { to: path, "aria-current": isActive ? 'page' : undefined, className: `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive
                                                        ? 'bg-primary text-white'
                                                        : 'text-text-secondary hover:bg-bg-primary'}`, children: [_jsx("span", { "aria-hidden": "true", children: item.icon }), _jsx("span", { children: item.label })] }) }, item.key));
                                        }) })] }))] }), _jsxs("div", { className: "p-4 border-t border-card-border", children: [user && (_jsxs("div", { className: "flex items-center gap-3 mb-3", children: [_jsx(Avatar, { userId: user.id, size: "w-10 h-10" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-text-primary truncate", children: user.displayName }), _jsx("p", { className: "text-xs text-text-muted capitalize", children: user.role })] })] })), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("p", { className: "text-xs text-text-muted", children: [t('common.version'), " ", String(window.__APP_VERSION__ || '1.0.0')] }), user && (_jsx("button", { onClick: handleLogout, className: "text-xs text-red-500 hover:text-red-600 transition-colors", children: t('auth.signOut') }))] })] })] })] }));
}
//# sourceMappingURL=Sidebar.js.map